import { memo, useCallback, useMemo, useState } from "react";

import { ExpandLess, ExpandMore, Visibility } from "@mui/icons-material";
import { Box, Collapse, IconButton, Paper, Typography } from "@mui/material";

import { formatPropertyPageValue, formatSnakeCaseToUserFriendly } from "../../utility/formatters";
import ChunkImageDialog from "./ChunkImageDialog";

/**
 * JsonDocumentViewer - Displays JSON data in a hierarchical, visually structured way
 *
 * Hierarchy:
 * - Level 0 (Root): Header (myobj.name || "---"), not in container
 * - Level 1: First level properties from root (objects/arrays/simple values)
 * - Level 2: Full-width banner "header row" (array elements)
 * - Level 3: Container not indented
 * - Level 4: Indented, minimal container
 * - Level 5+: Indented text, progressively greyed out
 */

const JsonDocumentViewer = ({ data, chunkUrls = [], documentId = null, globalState = null }) => {
  const [expanded, setExpanded] = useState({});
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const toggleExpand = useCallback((path, currentIsExpanded) => {
    setExpanded((prev) => {
      // Use the current computed expanded state if provided, otherwise toggle the stored value
      const newValue = currentIsExpanded !== undefined ? !currentIsExpanded : !prev[path];
      return {
        ...prev,
        [path]: newValue,
      };
    });
  }, []);

  const handleImageDialogOpen = useCallback(
    (imageUrl) => {
      const index = chunkUrls.indexOf(imageUrl);
      setCurrentImageIndex(index >= 0 ? index : 0);
      setImageDialogOpen(true);
    },
    [chunkUrls]
  );

  const handleImageDialogClose = useCallback(() => {
    setImageDialogOpen(false);
  }, []);

  const handleIndexChange = useCallback((newIndex) => {
    setCurrentImageIndex(newIndex);
  }, []);

  const isExpandable = useCallback((value) => {
    return value && typeof value === "object" && !Array.isArray(value) && Object.keys(value).length > 0;
  }, []);

  const formatValue = useCallback((value, key = "") => {
    // Handle undefined specifically (null is handled by formatPropertyPageValue)
    if (value === undefined) return "undefined";
    return formatPropertyPageValue(value, key);
  }, []);

  // Memoize the entire render tree based on data and expanded state
  const content = useMemo(() => {
    if (!data || typeof data !== "object") {
      return (
        <Typography color="text.secondary" variant="body2" sx={{ p: 2 }}>
          No data to display
        </Typography>
      );
    }

    // Level 3+: Container not indented (level 3), then indented minimal container (level 4+)
    const renderLevel3 = (key, value, path, level = 3) => {
      const isExp = expanded[path] ?? false; // Default collapsed for deeper levels
      const canExpand = isExpandable(value);

      // Skip the "name" field if we're at level 3 (already shown in banner)
      if (level === 3 && (key === "name" || key === "Name")) {
        return null;
      }

      // Handle arrays at deeper levels
      if (Array.isArray(value)) {
        return (
          <Box key={path} sx={{ mb: 1.5, ml: level > 3 ? 2 : 0 }}>
            {value.map((item, index) => {
              if (typeof item === "object" && item !== null && !Array.isArray(item)) {
                return renderArrayItem(item, index, `${path}[${index}]`, level);
              }
              return (
                <Box key={index} sx={{ mb: 0.5 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "12px",
                      color: level > 4 ? "text.disabled" : "text.primary",
                    }}
                  >
                    {formatSnakeCaseToUserFriendly(key)} [{index}]: {formatValue(item, key)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        );
      }

      if (canExpand) {
        // Check if we should display the value when collapsed (for levels 2-3)
        const showValueWhenCollapsed = !isExp && (level === 2 || level === 3) && value?.value !== undefined;

        return (
          <Paper
            key={path}
            elevation={level === 3 ? 1 : 0}
            sx={{
              mb: 1.5,
              ml: level > 3 ? 2 : 0,
              p: level === 3 ? 2 : 1,
              border: level > 3 ? "1px solid" : "none",
              borderColor: "divider",
              bgcolor: level > 3 ? "transparent" : "background.paper",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
                transition: "all 0.2s ease",
                "&:hover": {
                  bgcolor: "action.hover",
                  transform: "translateX(2px)",
                },
                borderRadius: "4px",
                p: 0.5,
                mx: -0.5, // Extend clickable area to edges
              }}
              onClick={() => toggleExpand(path, isExp)}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: "13px",
                  color: level > 4 ? "text.disabled" : "text.primary",
                }}
              >
                {formatSnakeCaseToUserFriendly(key)}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {showValueWhenCollapsed && (
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      fontSize: "13px",
                      color: level > 4 ? "text.disabled" : "text.primary",
                    }}
                  >
                    {formatValue(value.value, key)}
                  </Typography>
                )}
                <IconButton size="small" sx={{ pointerEvents: "none" }}>
                  {isExp ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                </IconButton>
              </Box>
            </Box>
            <Collapse in={isExp}>
              <Box sx={{ mt: 0.5 }}>
                {Object.entries(value)
                  .filter(([k]) => !k.startsWith("_"))
                  .map(([k, v]) => renderLevel3(k, v, `${path}.${k}`, level + 1))}
              </Box>
            </Collapse>
          </Paper>
        );
      }

      // Leaf node - display as key-value pair
      return (
        <Box
          key={path}
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(150px, 40%) 1fr",
            gap: 2,
            mb: 0.75,
            ml: level > 3 ? 2 : 0,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              fontSize: "12px",
              color: level > 4 ? "text.disabled" : "text.secondary",
            }}
          >
            {formatSnakeCaseToUserFriendly(key)}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: "12px",
              color: level > 4 ? "text.disabled" : "text.primary",
              wordBreak: "break-word",
            }}
          >
            {formatValue(value, key)}
          </Typography>
        </Box>
      );
    };

    // Generic array item renderer that respects nesting levels
    const renderArrayItem = (item, index, path, level = 2) => {
      const isExp = expanded[path] ?? level <= 2; // Default expanded for levels 2 and below
      const itemName = formatSnakeCaseToUserFriendly(item?.name || item?.Name || `Item ${index + 1}`);
      const canExpand = item && typeof item === "object" && !Array.isArray(item) && Object.keys(item).length > 0;

      // Determine styling based on level
      const getBannerStyles = () => {
        if (level === 2) {
          return {
            bgcolor: "primary.main",
            color: "primary.contrastText",
            padding: "8px 16px",
            fontSize: "14px",
            fontWeight: 600,
            darkBgcolor: "primary.dark",
          };
        } else {
          // Level 3+
          return {
            bgcolor: "action.selected",
            color: "text.primary",
            padding: "6px 12px",
            fontSize: "13px",
            fontWeight: 500,
            darkBgcolor: "action.selected",
          };
        }
      };

      const styles = getBannerStyles();

      // Check if we should display the value when collapsed (for levels 2-3)
      const showValueWhenCollapsed = !isExp && (level === 2 || level === 3) && item?.value !== undefined;

      return (
        <Box key={path} sx={{ mb: level === 2 ? 2 : 1.5, ml: level > 3 ? 2 : 0 }}>
          <Box
            data-level={level}
            sx={{
              width: "100%",
              bgcolor: styles.bgcolor,
              color: styles.color,
              padding: styles.padding,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: canExpand ? "pointer" : "default",
              borderRadius: "4px",
              transition: "all 0.2s ease",
              "&:hover": canExpand
                ? {
                    opacity: 0.9,
                    transform: "translateX(2px)",
                  }
                : {},
              "[data-mui-color-scheme='dark'] &": {
                bgcolor: styles.darkBgcolor,
                color: "primary.contrastText",
              },
            }}
            onClick={() => canExpand && toggleExpand(path, isExp)}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: styles.fontWeight, fontSize: styles.fontSize }}>
              {itemName}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {showValueWhenCollapsed && (
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    fontSize: styles.fontSize,
                    opacity: 0.9,
                  }}
                >
                  {formatValue(item.value, "value")}
                </Typography>
              )}
              {chunkUrls?.length > 1 && chunkUrls[item?._rg_chunk - 1] && (
                <Visibility
                  sx={{
                    fontSize: "18px",
                    cursor: "pointer",
                    opacity: 0.8,
                    "&:hover": {
                      opacity: 1,
                    },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleImageDialogOpen(chunkUrls[item?._rg_chunk - 1]);
                  }}
                />
              )}
              {canExpand && (
                <IconButton size="small" sx={{ color: "inherit", pointerEvents: "none" }}>
                  {isExp ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              )}
            </Box>
          </Box>
          {canExpand && (
            <Collapse in={isExp}>
              <Box sx={{ mt: 1 }}>
                {Object.entries(item)
                  .filter(([k]) => !k.startsWith("_"))
                  .map(([k, v]) => renderLevel3(k, v, `${path}.${k}`, level + 1))}
              </Box>
            </Collapse>
          )}
        </Box>
      );
    };

    // Level 2 - Array element banner (second level)
    const renderLevel2 = (item, index, path) => {
      return renderArrayItem(item, index, path, 2);
    };

    // Level 1 property - first level properties from root
    const renderLevel1 = (key, value, path) => {
      // Skip the "name" field if it's already shown as document name
      if (key === "name" || key === "Name") {
        return null;
      }

      // Handle arrays - render each element as a banner (second level)
      if (Array.isArray(value)) {
        return (
          <Box key={path} sx={{ mb: 2 }}>
            {value.map((item, index) => renderLevel2(item, index, `${path}[${index}]`))}
          </Box>
        );
      }

      // Handle objects - render as first level banner (darker background)
      if (isExpandable(value)) {
        const isExp = expanded[path] ?? true;
        return (
          <Box key={path} sx={{ mb: 2 }}>
            <Box
              data-level="1"
              sx={{
                width: "100%",
                bgcolor: "primary.main",
                color: "primary.contrastText",
                padding: "8px 16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
                borderRadius: "4px",
                transition: "all 0.2s ease",
                "&:hover": {
                  bgcolor: "primary.dark",
                  transform: "translateX(2px)",
                },
                "[data-mui-color-scheme='dark'] &": {
                  bgcolor: "primary.dark",
                  color: "primary.contrastText",
                },
                "[data-mui-color-scheme='dark'] &:hover": {
                  bgcolor: "primary.main",
                },
              }}
              onClick={() => toggleExpand(path, isExp)}
            >
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "16px" }}>
                {formatSnakeCaseToUserFriendly(key)} - {value?.name ? formatSnakeCaseToUserFriendly(value.name) : ""}
              </Typography>
              <IconButton size="small" sx={{ color: "primary.contrastText", pointerEvents: "none" }}>
                {isExp ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
            <Collapse in={isExp}>
              <Box sx={{ mt: 1 }}>
                {Object.entries(value)
                  .filter(([k]) => !k.startsWith("_"))
                  .map(([k, v]) => renderLevel3(k, v, `${path}.${k}`))}
              </Box>
            </Collapse>
          </Box>
        );
      }

      // Simple value
      return (
        <Box
          key={path}
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(150px, 40%) 1fr",
            gap: 2,
            mb: 1,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "13px", color: "text.secondary" }}>
            {formatSnakeCaseToUserFriendly(key)}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: "13px",
              color: "text.primary",
              wordBreak: "break-word",
            }}
          >
            {formatValue(value, key)}
          </Typography>
        </Box>
      );
    };

    // Root level: Just header, no container
    const renderRoot = (data) => {
      const documentName = formatSnakeCaseToUserFriendly(data.name || data.Name || "---");

      return (
        <Box>
          {/* Top level header */}
          <Typography variant="h5" sx={{ fontWeight: 600, fontSize: "18px", mb: 2, color: "text.primary" }}>
            {documentName}
          </Typography>
          {/* Render all properties as level 1 (first level) */}
          {Object.entries(data)
            .filter(([key]) => !key.startsWith("_"))
            .map(([key, value]) => renderLevel1(key, value, key))}
        </Box>
      );
    };

    return <Box sx={{ p: 2 }}>{renderRoot(data)}</Box>;
  }, [data, chunkUrls, expanded]);

  return (
    <>
      {content}
      <ChunkImageDialog
        open={imageDialogOpen}
        onClose={handleImageDialogClose}
        imageUrl={chunkUrls[currentImageIndex]}
        chunkUrls={chunkUrls}
        currentIndex={currentImageIndex}
        onIndexChange={handleIndexChange}
        showNavigation={true}
        documentId={documentId}
        globalState={globalState}
      />
    </>
  );
};

export default memo(JsonDocumentViewer);
